import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Container } from "reactstrap";
import { Row, Col, Button } from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { PAGE_TITLES } from "../Common/constants";
import {
  setDocumentSuccessMessage,
  setEditSuccessMessage,
  setEmailUpdateSuccessMessage,
  setSuccessMessage,
} from "../../slices/toastMessage/action";
import { Loader } from "../Utils/Loader";
import { fetchAllUom } from "../../slices/uom/thunk";
import BreadCrumb from "../Common/BreadCrumb";
import { operatorColoumns } from "./Constant/OperatorColoumn";
import { extractedData } from "./Constant/OperatorListingJson";
import { MenuItem } from "@mui/material";
import { fetchAllOperatorList } from "../../slices/OperatorManagement/thunk";
import { setWelcomeToast } from "../../slices/welcomeToaster/action";
import { useColumnSettings } from "../Utils/useColumnSettings";
import { getView } from "../../slices/View/thunk";
import { hasPermission } from "../Utils/UtilityFunctions";

function OperatorList() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  let navigate = useNavigate();
  const columns = operatorColoumns();
  const dispatch = useDispatch();
  const showSuccessMessage = useSelector(
    (state) => state.SuccessToast.showSuccessMessage
  );
  const showEditSuccessMessage = useSelector(
    (state) => state.SuccessToast.showEditSuccessMessage
  );
  const showEmailUpdateSuccessMessage = useSelector(
    (state) => state.SuccessToast.showEmailUpdateSuccessMessage
  );
  const showDocumentSuccessMessage = useSelector(
    (state) => state.SuccessToast.showDocumentSuccessMessage
  );
  const showWelcomeToast = useSelector(
    (state) => state.welcomeToast.showWelcomeToast
  );

  useEffect(() => {
    dispatch(fetchAllOperatorList(setLoading));
    dispatch(getView(setLoading));
  }, [dispatch]);

  useEffect(() => {
    if (showWelcomeToast) {
      toast.success(`Welcome ${localStorage.getItem("userName")}`);
      dispatch(setWelcomeToast(false));
    }
  }, [showWelcomeToast, dispatch]);
  const OperatorList = useSelector(
    (state) => state.GetOperatorList.OperatorList
  );
  const updatedData = useMemo(() => {
    const listingData = extractedData(OperatorList);
    return listingData.map((item) => ({
      ...item,
      status:
        item.status === "validated"
          ? "Active"
          : item.status === "initialized"
            ? "Inactive"
            : "Closed",
    }));
  }, [OperatorList]);
  const navigateToAddOperator = () => {
    let path = "/create-operator";
    navigate(path);
  };
  useEffect(() => {
    if (showSuccessMessage) {
      toast.success(`Operator Created Successfully`);
      dispatch(setSuccessMessage(false));
    }
    if (showEditSuccessMessage) {
      toast.success(`Operator Updated Successfully`);
      dispatch(setEditSuccessMessage(false));
    }
    if (showDocumentSuccessMessage) {
      toast.success(`Document Uploaded Successfully`);
      dispatch(setDocumentSuccessMessage(false));
    }
    if (showEmailUpdateSuccessMessage) {
      toast.success(`Verification link has been sent to your updated email`);
      dispatch(setEmailUpdateSuccessMessage(false));
    }
  }, [
    showSuccessMessage,
    showEditSuccessMessage,
    showEmailUpdateSuccessMessage,
    dispatch,
  ]);

  useEffect(() => {
    const filteredData = updatedData.filter((row) => {
      const globalFilterLower = globalFilter.toLowerCase();
      const columnFilterChecks = columnFilters.map((filter) => {
        const filterValue = filter?.value?.toLowerCase();
        const cellValue = row[filter.id]?.toString().toLowerCase();
        if (filter.id === "status") {
          return cellValue === filterValue;
        }
        return cellValue?.includes(filterValue);
      });

      const matchesGlobalFilter =
        !globalFilter ||
        (row?.endDateTime &&
          row?.endDateTime?.toLowerCase()?.includes(globalFilterLower)) ||
        (row?.operatorId &&
          row?.operatorId?.toLowerCase()?.includes(globalFilterLower)) ||
        (row?.organizationType &&
          row?.organizationType?.toLowerCase()?.includes(globalFilterLower)) ||
        (row?.startDateTime &&
          row?.startDateTime?.toLowerCase()?.includes(globalFilterLower)) ||
        (row?.status && row?.status?.toLowerCase() === globalFilterLower) || // Exact match for status
        (row?.tradingName &&
          row?.tradingName?.toLowerCase()?.includes(globalFilterLower));

      return matchesGlobalFilter && columnFilterChecks.every(Boolean);
    });
    setData(filteredData);
  }, [globalFilter, columnFilters, updatedData]);

  const handleCancel = () => {
    setGlobalFilter("");
    setColumnFilters([]);
    setData(updatedData);
  };

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
    if (!filter) {
      handleCancel();
    }
  };

  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap
  );

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)])
  );
  const operatorPermission = hasPermission(permissionsMap, "operators", "operator");
  const viewPermission = hasPermission(permissionsMap, "miscellaneous", "view")

  const {
    settingsModalOpen,
    viewColumns,
    toggleSettingsModal,
    SettingsSidebar,
    CustomViewModal,
  } = useColumnSettings("operator", columns);

  return (
    <React.Fragment>
      <ToastContainer position="top-center" />
      <div id="csms" className="page-content">
        <Container fluid>
          <BreadCrumb
            title={"Operator List"}
            pageTitle={"Operators"}
            moduleTitle={"Operators"}
          />
          <Row>
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100">
              <div className="d-flex flex-column flex-md-row">
                <h5 className="rv-page-title p-1">{"Operator List"}</h5>
              </div>
              <div className="d-flex align-items-center">
                {operatorPermission &&
                  operatorPermission?.has("create") ? (
                  <Button
                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={navigateToAddOperator}
                  >
                    <i className="ri-add-fill label-icon align-middle fs-16 me-1 fw-bold"></i>
                    <span className="ps-2 ms-2 me-1 fw-bold">
                      {"Add Operator"}
                    </span>
                  </Button>
                ) : null}
                {viewPermission && viewPermission?.has("view") &&
                  <div
                    color="secondary"
                    className="btn-icon cursor-img"
                    onClick={toggleSettingsModal}
                  >
                    <i className="ri-settings-2-line fs-4"></i>
                  </div>}
              </div>
            </div>
            <Col>
              <div className="listingjs-table " id="customerList">
                <div className="my-3 mx-1 table-responsive border">
                  {loading && <Loader />}
                  {OperatorList ? (
                    <MaterialReactTable
                      enableRowActions
                      displayColumnDefOptions={{
                        "mrt-row-actions": {
                          header: "",
                          enableHiding: true,
                          size: 53,
                          grow: true,
                        },
                      }}
                      defaultColumn={{
                        minSize: 10,
                        maxSize: 9001,
                        muiTableBodyCellProps: {
                          sx: {
                            fontFamily: "Inter,sans-serif",
                          },
                        },
                        muiTableHeadCellProps: {
                          sx: {
                            fontFamily: "Inter,sans-serif",
                          },
                        },
                      }}
                      renderRowActionMenuItems={({ row, closeMenu }) => [
                        operatorPermission && operatorPermission?.has("view") ?
                          <MenuItem
                            key={`${row.index}.1`}
                            onClick={() => {
                              navigate(
                                `/view-operator/${row.original.operatorId}`
                              );
                            }}
                          >
                            <i className="ri-eye-line me-2"></i>View
                          </MenuItem> : null,
                        operatorPermission && operatorPermission?.has("update") ?
                          <MenuItem
                            key={`${row.index}.2`}
                            onClick={() => {
                              navigate(
                                `/edit-operator/${row.original.operatorId}`
                              );
                            }}
                          >
                            <i className="ri-edit-box-line me-2" /> Edit
                          </MenuItem> : null,
                      ]}
                      initialState={{
                        columnVisibility: {
                          operatorId: false,
                        },
                      }}
                      columns={columns.filter((column) =>
                        viewColumns?.includes(
                          column.accessorKey || column.header
                        )
                      )}
                      data={data}
                      manualFiltering
                      enableTopToolbar={false}
                      onColumnFiltersChange={setColumnFilters}
                      onGlobalFilterChange={handleGlobalFilterChange}
                      state={{ globalFilter }}
                      enableColumnOrdering={false}
                      enableColumnDragging={false}
                      enableDensityToggle={false}
                      enableColumnActions={true}
                    />
                  ) : null}
                </div>
              </div>
            </Col>
            {CustomViewModal()}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
export default OperatorList;
